import { supabase } from '../lib/supabase';
import { getTimezoneForCoordinates } from './timezoneService';
import { identifyUser as enchargeIdentify } from './enchargeService';
import { setViewerTimezoneOverride } from '../utils/timeHelpers';

// User-profile state for the active user. Backed by public.user_profiles,
// keyed by user_id (PK references auth.users.id). RLS restricts every read
// and write to the authenticated user's own row.

const getCurrentUser = async (providedUser = null) => {
  if (providedUser) return providedUser;
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

/**
 * Read the user's onboarding-completed flag.
 *
 * Returns `false` when there's no profile row yet (the typical first-login
 * state) or if the lookup fails. ProtectedLayout treats `false` as "the gate
 * should send them to /onboarding".
 *
 * @returns {Promise<boolean>}
 */
export const getOnboardingCompleted = async (currentUser = null) => {
  try {
    const user = await getCurrentUser(currentUser);
    if (!user) return false;

    const { data, error } = await supabase
      .from('user_profiles')
      .select('onboarding_completed')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) {
      console.error('Error fetching user_profiles row:', error);
      return false;
    }

    return !!data?.onboarding_completed;
  } catch (err) {
    console.error('Exception in getOnboardingCompleted:', err);
    return false;
  }
};

/**
 * Read the full `user_profiles` row for the current user. Returns null when
 * no row exists yet or on error. Caller decides how to handle empty fields.
 *
 * @returns {Promise<object|null>}
 */
export const getUserProfile = async (currentUser = null) => {
  try {
    const user = await getCurrentUser(currentUser);
    if (!user) return null;

    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) {
      console.error('Error fetching user_profiles row:', error);
      return null;
    }

    return data || null;
  } catch (err) {
    console.error('Exception in getUserProfile:', err);
    return null;
  }
};

/**
 * Persist the user's home golf course plus the country and timezone derived
 * from it. `course` is whatever `GolfClubAutocomplete` returns - typically a
 * row from `golf_clubs` with id/name/latitude/longitude/country_code, but
 * may be a free-text string when no DB match exists (in which case we
 * record just the name).
 *
 * @returns {Promise<{ data: any, error: any }>}
 */
export const setUserHomeCourse = async (course, currentUser = null) => {
  try {
    const user = await getCurrentUser(currentUser);
    if (!user) return { data: null, error: 'User not authenticated' };

    const courseObj = typeof course === 'string' ? { name: course } : (course || {});
    const homeCourseId = courseObj.id || null;
    const homeCourseName = (courseObj.name || '').trim() || null;
    const countryCode = (courseObj.country_code || '').trim().toUpperCase() || null;

    let timezone = null;
    if (courseObj.latitude != null && courseObj.longitude != null) {
      timezone = await getTimezoneForCoordinates(courseObj.latitude, courseObj.longitude);
    }

    const payload = {
      user_id: user.id,
      home_course_id: homeCourseId,
      home_course_name: homeCourseName,
      country_code: countryCode,
      timezone,
    };

    const { data, error } = await supabase
      .from('user_profiles')
      .upsert(payload, { onConflict: 'user_id' })
      .select()
      .single();

    if (error) {
      console.error('Error saving home course to user_profiles:', error);
      return { data: null, error };
    }

    enchargeIdentify(user, {
      homeCourse: homeCourseName,
      countryCode,
      timezone,
    });

    return { data, error: null };
  } catch (err) {
    console.error('Exception in setUserHomeCourse:', err);
    return { data: null, error: err };
  }
};

/**
 * Update just the user's country_code. Used by Settings when the user wants
 * to override the value derived from their home course.
 *
 * @returns {Promise<{ data: any, error: any }>}
 */
export const setUserCountry = async (countryCode, currentUser = null) => {
  try {
    const user = await getCurrentUser(currentUser);
    if (!user) return { data: null, error: 'User not authenticated' };

    const normalised = (countryCode || '').trim().toUpperCase() || null;

    const { data, error } = await supabase
      .from('user_profiles')
      .upsert(
        { user_id: user.id, country_code: normalised },
        { onConflict: 'user_id' },
      )
      .select()
      .single();

    if (error) {
      console.error('Error saving country to user_profiles:', error);
      return { data: null, error };
    }

    enchargeIdentify(user, { countryCode: normalised });

    return { data, error: null };
  } catch (err) {
    console.error('Exception in setUserCountry:', err);
    return { data: null, error: err };
  }
};

/**
 * Update just the user's timezone (IANA name). Used by Settings when the
 * user wants to override the value derived from their home course.
 *
 * @returns {Promise<{ data: any, error: any }>}
 */
export const setUserTimezone = async (timezone, currentUser = null) => {
  try {
    const user = await getCurrentUser(currentUser);
    if (!user) return { data: null, error: 'User not authenticated' };

    const normalised = (timezone || '').trim() || null;

    const { data, error } = await supabase
      .from('user_profiles')
      .upsert(
        { user_id: user.id, timezone: normalised },
        { onConflict: 'user_id' },
      )
      .select()
      .single();

    if (error) {
      console.error('Error saving timezone to user_profiles:', error);
      return { data: null, error };
    }

    enchargeIdentify(user, { timezone: normalised });
    // Update the display-helper override so the change takes effect on the
    // current screen immediately — otherwise the user has to reload to see
    // "your local time" update.
    setViewerTimezoneOverride(normalised);

    return { data, error: null };
  } catch (err) {
    console.error('Exception in setUserTimezone:', err);
    return { data: null, error: err };
  }
};

/**
 * Mark the user as having completed onboarding. Upserts a `user_profiles`
 * row keyed by user_id, setting `onboarding_completed = true`.
 *
 * @returns {Promise<{ data: any, error: any }>}
 */
/**
 * Backfill the user's timezone and country_code from device locale when
 * they're missing. Used to populate segmentation columns for users who
 * onboarded before those columns existed. Never overwrites an existing
 * value - the onboarding flow's home-course choice is the source of truth.
 */
export const backfillLocaleIfMissing = async (currentUser = null) => {
  try {
    const user = await getCurrentUser(currentUser);
    if (!user) return;

    const profile = await getUserProfile(user);
    // No profile row yet - let onboarding create it; nothing to patch.
    if (!profile) return;

    const patch = {};

    if (!profile.timezone) {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz) patch.timezone = tz;
      } catch {
        // ignore - older runtimes
      }
    }

    if (!profile.country_code) {
      const lang = (typeof navigator !== 'undefined' && navigator.language) || '';
      const region = lang.split('-')[1];
      if (region && region.length === 2) {
        patch.country_code = region.toUpperCase();
      }
    }

    if (Object.keys(patch).length === 0) return;

    const { error } = await supabase
      .from('user_profiles')
      .upsert({ user_id: user.id, ...patch }, { onConflict: 'user_id' });

    if (error) {
      console.warn('backfillLocaleIfMissing upsert failed:', error);
      return;
    }

    enchargeIdentify(user, {
      ...(patch.timezone ? { timezone: patch.timezone } : {}),
      ...(patch.country_code ? { countryCode: patch.country_code } : {}),
    });
  } catch (err) {
    console.warn('Exception in backfillLocaleIfMissing:', err);
  }
};

export const markOnboardingCompleted = async (currentUser = null) => {
  try {
    const user = await getCurrentUser(currentUser);
    if (!user) return { data: null, error: 'User not authenticated' };

    const { data, error } = await supabase
      .from('user_profiles')
      .upsert(
        { user_id: user.id, onboarding_completed: true },
        { onConflict: 'user_id' },
      )
      .select()
      .single();

    if (error) {
      console.error('Error marking onboarding completed:', error);
      return { data: null, error };
    }

    enchargeIdentify(user, { onboardingCompleted: true });

    return { data, error: null };
  } catch (err) {
    console.error('Exception in markOnboardingCompleted:', err);
    return { data: null, error: err };
  }
};
